import React, { useEffect, useState } from "react";
import { Row, Col, Form, Button, Container, Image } from "react-bootstrap";
import { useDispatch } from "react-redux";
import { useDialog } from "../../../../helpers/useDialog";
import Select from "react-select";
import PageSpinner from "../../../common/PageSpinner";
import { BHAREE_BASE_URL_LOCAL } from "../../../../config/config.url";
import axios from "axios";
import CustomTable from "../../common/CustomTable";
import { tableStructures } from "../../../../helpers/table-structures";
import {
  exportPayoutOrdersToCSV,
  exportPayoutOrdersToExcel,
} from "../../../../utils/exportOrdersToCSV";

const SellerPayouts = ({ title = "Payouts", userId, userObj }) => {
  const dispatch = useDispatch();
  const { alert, confirm, DialogModal } = useDialog();
  const [errorMsg, setErrorMsg] = useState("");
  const [loading, setLoading] = useState(false);

  const [payoutData, setPayoutData] = useState([]);

  const currentYear = new Date().getFullYear();
  const currentMonth = new Date().getMonth() + 1; // 1 - 12

  const [yearOptions, setYearOptions] = useState([]);
  const [monthOptions, setMonthOptions] = useState([]);
  const [selectedYear, setSelectedYear] = useState(null);
  const [selectedMonth, setSelectedMonth] = useState(null);

  // Create year options and set default selected year
  useEffect(() => {
    let yrs = [];
    for (let y = 2025; y <= currentYear; y++) {
      yrs.push({ value: y, label: y.toString() });
    }
    setYearOptions(yrs);

    // Set default year as current year
    setSelectedYear({ value: currentYear, label: currentYear.toString() });
  }, [currentYear]);

  // Update month options depending on selected year
  useEffect(() => {
    if (!selectedYear) return;

    const maxMonth = selectedYear.value === currentYear ? currentMonth : 12;

    const months = Array.from({ length: maxMonth }, (_, i) => ({
      value: i + 1,
      label: new Date(2024, i).toLocaleString("en-US", { month: "long" }),
    }));

    setMonthOptions(months);

    // Set default month only on first load or if selected year is current-year
    if (selectedYear.value === currentYear) {
      setSelectedMonth({
        value: currentMonth,
        label: new Date(2024, currentMonth - 1).toLocaleString("en-US", { month: "long" }),
      });
    } else {
      setSelectedMonth(null); // reset for past years
    }
  }, [selectedYear, currentYear, currentMonth]);

  let options = {
    headers: {
      "Content-Type": "application/json",
      "Access-Control-Allow-Origin": "*",
      Authorization: `Bearer ${localStorage.jwtToken}`,
    },
  };

  const showPayouts = async () => {
    console.log("SellerID: ", userId, "Year: ", selectedYear, "Month: ", selectedMonth);
    setLoading(true);
    try {
      const response = await axios.get(
        `${BHAREE_BASE_URL_LOCAL}/admin/getPayoutData?month=${selectedMonth.value}&year=${selectedYear.value}&sellerId=${userId}`,
        options,
      );

      console.log("RESPONSE: ", response?.data);

      if (response && response?.data && response?.data?.success) {
        setPayoutData(response?.data?.data?.orders);
      } else {
        setPayoutData([]);
        await alert("No data found!");
      }
    } catch (error) {
      console.error("Show Payouts error:", error.response || error.message);
      throw error;
    } finally {
      setLoading(false);
    }
  };

  const markPayoutDone = async (row) => {
    const isConfirmed = await confirm(
      `Are you sure you want to mark the payout done for TI NO: ${row.original.invoiceNumber} and Order: #${row.original.orderNumber}?`,
      "",
    );
    if (isConfirmed) {
      setLoading(true);
      try {
        const response = await axios.get(
          `${BHAREE_BASE_URL_LOCAL}/admin/markPayoutDone/${row.original.orderId}`,
          options,
        );

        if (response && response?.data && response?.data?.success) {
          showPayouts();
        }
      } catch (error) {
        console.error("Show Payouts error:", error.response || error.message);
        throw error;
      } finally {
        setLoading(false);
      }
    }
  };

  const markAllDone = async () => {
    const isConfirmed = await confirm(
      `Are you sure you want to mark the payout done for all the invoices for the selected month and year?`,
      "",
    );
    if (isConfirmed) {
      setLoading(true);
      try {
        const response = await axios.post(
          `${BHAREE_BASE_URL_LOCAL}/admin/markAllPayoutDone`,
          {
            sellerId: userId,
            month: selectedMonth.value,
            year: selectedYear.value,
          },
          options,
        );

        console.log("RESPONSE: ", response?.data);

        if (response && response?.data && response?.data?.success) {
          showPayouts();
        }
      } catch (error) {
        console.error("Show Payouts error:", error.response || error.message);
        throw error;
      } finally {
        setLoading(false);
      }
    }
  };

  return (
    <>
      {DialogModal}
      {loading && <PageSpinner />}
      <Container fluid className="py-3 bg-white seller-payouts">
        <Row>
          <Col md={2} className="mt-2">
            <Select
              options={yearOptions}
              placeholder="Select Year"
              value={selectedYear}
              onChange={(val) => setSelectedYear(val)}
              menuPortalTarget={document.body} // ⬅️ portal the menu to body
              styles={{
                menuPortal: (base) => ({ ...base, zIndex: 9999 }),
                // ⬅️ ensure text is left aligned
                singleValue: (base) => ({
                  ...base,
                  textAlign: "left",
                }),

                placeholder: (base) => ({
                  ...base,
                  textAlign: "left",
                }),

                valueContainer: (base) => ({
                  ...base,
                  justifyContent: "flex-start",
                }),

                // optional (sometimes needed)
                control: (base) => ({
                  ...base,
                  textAlign: "left",
                }),
              }}
            />
          </Col>
          <Col md={2} className="mt-2">
            <Select
              options={monthOptions}
              placeholder="Select Month"
              value={selectedMonth}
              isDisabled={!selectedYear}
              onChange={(val) => setSelectedMonth(val)}
              menuPortalTarget={document.body} // ⬅️ portal the menu to body
              styles={{
                menuPortal: (base) => ({ ...base, zIndex: 9999 }),
                // ⬅️ ensure text is left aligned
                singleValue: (base) => ({
                  ...base,
                  textAlign: "left",
                }),

                placeholder: (base) => ({
                  ...base,
                  textAlign: "left",
                }),

                valueContainer: (base) => ({
                  ...base,
                  justifyContent: "flex-start",
                }),

                // optional (sometimes needed)
                control: (base) => ({
                  ...base,
                  textAlign: "left",
                }),
              }}
            />
          </Col>
          <Col md={2} className="text-start mt-2">
            <Button variant="primary" onClick={() => showPayouts()}>
              Show Payouts
            </Button>
          </Col>
          <Col md={6} className="text-end mt-2">
            <Button variant="outline-success" onClick={() => markAllDone()}>
              <i className="bi bi-check-lg"></i> Mark All Done
            </Button>
            <Button
              variant="outline-primary"
              className="ms-2"
              onClick={() => exportPayoutOrdersToCSV(payoutData)}
            >
              <i className="bi bi-download"></i> Download CSV
            </Button>
          </Col>
        </Row>
        <Row className="m-0 p-0 mt-2">
          <Col md={12} className="m-0 p-0">
            <CustomTable
              className="table-custom-class"
              columns={tableStructures.seller_payouts}
              data={payoutData}
              enableRowActions={true}
              positionActionsColumn={"last"}
              enableTopToolbar={false}
              emptyTableMsg={""}
              enablePagination={false}
              enableStickyFooter={true}
              initialState={{
                columnPinning: {
                  right: ["mrt-row-actions"],
                },
              }}
              renderRowActions={({ row }) => (
                <div className="table-action-box">
                  {!row.original.payout ? (
                    <Button variant="primary" size="sm" onClick={() => markPayoutDone(row)}>
                      <i className="bi bi-check-lg"></i>
                    </Button>
                  ) : (
                    ""
                  )}
                </div>
              )}
              renderTopToolbarCustomActions={() => (
                <div className="table-heading-wrap">
                  <h4>{"Payouts"}</h4>
                </div>
              )}
              muiTableHeadCellProps={{
                sx: {
                  borderTop: "1px solid #ccc",
                  borderRight: "1px solid #ccc",
                  borderLeft: "1px solid #ccc",
                  padding: "8px !important",
                  fontWeight: "bold",
                },
              }}
              muiTableBodyCellProps={({ row, column }) => ({
                sx: {
                  borderRight: "1px solid #ccc",
                  borderLeft: "1px solid #ccc",
                  padding: "8px !important",
                  backgroundColor: row.original.payout
                    ? "#d4edda !important"
                    : "#ffffff !important",
                },
              })}
              muiTableFooterCellProps={{
                sx: {
                  fontWeight: "bold", // footer total bold
                  fontSize: "16px",
                  fontFamily: "InterRegular",
                  paddingRight: "8px !important", // force footer to match
                  paddingLeft: "8px !important", // force footer to match
                  borderRight: "1px solid #ccc",
                  borderLeft: "1px solid #ccc",
                },
              }}
              muiTableFooterProps={{
                sx: { zIndex: 1 },
              }}
              //   enableColumnOrdering={false} //enable some features
              //   enableRowSelection={false}
              //   enableColumnResizing={false}
              //   onRowSelectionChange={setRowSelection} //hoist internal state to your own state (optional)
              //   state={{ rowSelection }} //manage your own state, pass it back to the table (optional)
              //   tableInstanceRef={tableInstanceRef} //get a reference to the underlying table instance (optional)
            />
          </Col>
        </Row>
      </Container>
    </>
  );
};

export default SellerPayouts;
